import { getStatusDisplayName } from '@/types/conversations'
import type { Mail, WhatsAppThreadSummary } from './data'

type Traducir = (key: string) => string

/**
 * Un contacto con varios numeros de WhatsApp ocupa UNA tarjeta en la bandeja
 * -la de su hilo mas reciente-, y los demas hilos viajan dentro de ella. Estas
 * tres funciones son lo unico que hace falta para tratar esa tarjeta como lo
 * que es: varias conversaciones bajo un mismo cliente.
 */

/** Los hilos hermanos de la tarjeta. Vacío cuando el contacto solo tiene uno. */
function hilosDe(mail: Mail): WhatsAppThreadSummary[] {
  return mail.whatsappThreads ?? []
}

/** Si el hilo abierto es esta tarjeta o uno de sus hermanos. */
export function cardContieneHilo(mail: Mail, conversationId: string | null): boolean {
  if (!conversationId) return false
  if (mail.id === conversationId) return true
  return hilosDe(mail).some((hilo) => hilo.id === conversationId)
}

/**
 * La conversacion abierta, buscando tanto por la cabeza de la tarjeta como por
 * cualquiera de sus hermanos.
 *
 * Sin esto, pulsar una linea del conmutador dejaba el panel en blanco: el id
 * elegido no era el de ninguna fila de la lista.
 */
export function resolveSelectedThread(mails: Mail[], selectedId: string | null): Mail | null {
  if (!selectedId) return null

  const cabeza = mails.find((mail) => mail.id === selectedId)
  if (cabeza) return cabeza

  for (const mail of mails) {
    const hermano = hilosDe(mail).find((hilo) => hilo.id === selectedId)
    if (!hermano) continue

    // La tarjeta con la identidad del hermano: mismo cliente, otro hilo.
    return {
      ...mail,
      id: hermano.id,
      status: hermano.status,
      canReply: hermano.canReply,
      // El motivo del bloqueo es de la cabeza, no del hermano: se pide al
      // abrirlo si hace falta, en vez de ensenar aqui uno que no le toca.
      replyBlockReason: undefined,
      phoneNumberId: hermano.phoneNumberId,
      line: hermano.line,
      unreadCount: hermano.unreadCount,
    }
  }

  return null
}

/**
 * Una tarjeta esta archivada solo si TODOS sus hilos lo estan.
 *
 * Con un hilo archivado y otro vivo, el cliente sigue activo: mandarlo a la
 * pestana de Archivadas lo esconderia teniendo conversacion abierta.
 */
export function isCardArchived(mail: Mail): boolean {
  const hilos = hilosDe(mail)
  if (hilos.length === 0) return mail.status === 'archived'
  return hilos.every((hilo) => hilo.status === 'archived')
}

/** Las no leidas del cliente: las de todos sus hilos, no solo las de la cabeza. */
export function cardUnreadCount(mail: Mail): number {
  const hilos = hilosDe(mail)
  if (hilos.length === 0) return mail.unreadCount || 0
  return hilos.reduce((total, hilo) => total + (hilo.unreadCount || 0), 0)
}

/** El nombre de la linea de un hilo; sin conexion viva que la nombre, se dice eso. */
function lineaDe(hilo: WhatsAppThreadSummary, t: Traducir): string {
  return hilo.line?.label || t('conversations.line_previous')
}

/**
 * Como se llama un hilo en las insignias y en el conmutador.
 *
 * Su linea, normalmente. Sin linea viva que la nombre -el numero es de una
 * conexion anterior- se dice eso y no el id crudo del numero. Y si otro hilo
 * del cliente comparte la linea (uno completado y otro nuevo en el mismo
 * numero, que es lo normal al cerrar una conversacion), el estado los
 * distingue: dos botones iguales no dicen cual abrir. Visto en produccion el
 * 2026-09-08 con un cliente que tenia un hilo completado y otro activo en la
 * misma linea.
 */
export function threadLabel(
  hilo: WhatsAppThreadSummary,
  hilos: WhatsAppThreadSummary[],
  t: Traducir,
): string {
  const linea = lineaDe(hilo, t)
  const repetida = hilos.some((otro) => otro.id !== hilo.id && lineaDe(otro, t) === linea)
  return repetida ? `${linea} · ${getStatusDisplayName(hilo.status, t)}` : linea
}

/** Las lineas de la tarjeta, para pintarlas como insignias. Vacio si solo hay una. */
export function cardLines(
  mail: Mail,
  t: Traducir,
): { id: string; label: string; unread: number; isInternal: boolean }[] {
  const hilos = hilosDe(mail)
  if (hilos.length <= 1) return []
  return hilos.map((hilo) => ({
    id: hilo.id,
    label: threadLabel(hilo, hilos, t),
    unread: hilo.unreadCount || 0,
    isInternal: Boolean(hilo.line?.isInternal),
  }))
}
